import { APIEntity, dataManager, isNonEmptyString, registerEntity } from '../APIEntity';
import { ActionInfo } from '../models/ActionInfo';
import type { CloudDeployProvider } from './agent';
import type { IEntity, EntityMerge } from '../IEntity';
import { editorForType } from '../models/asset-editor';
import { DockPointerData } from '../models/DockPointer';
import { normalizeKind } from '../models/Kind';
import { isTypeId, TypeId } from '../models/TypeId';
import { ViewType } from '../utils/ui/view-types';
import { WorldViewProjection } from '../worldview/projection';
import { DEFAULT_CREDENTIAL_ENVIRONMENT } from '../services/credentials-service';
import {
  type EndpointSubkind,
  type HealthCheck,
  type HealthState,
  type IServiceEndpoint,
  ServiceEndpoint,
  type ServiceBackend,
  type ServiceProtocol,
  worstHealth,
} from './service-endpoint';

export type ArtifactLinkSource = 'manual' | 'gcp_label';
export type DeploymentSyncState = 'current' | 'stale' | 'partial' | 'error';
export type DeploymentObservationKind = 'cost' | 'size' | 'activity';
export type ObservationCoverage = 'available' | 'unavailable' | 'unattributed' | 'stale';

/** Who a box logs in as for a deployment: nobody, the placed agent itself, or its owner. */
export type DeploymentIdentity = 'none' | 'agent' | 'user';

/**
 * Providers that place a resource on a ComputeNode, so `origin.external_id`
 * names that node. An inventoried `gcp` resource is not node-backed — its
 * `external_id` is the provider's own resource name.
 */
export const NODE_PROVIDERS: ReadonlySet<string> = new Set([
  'local',
  'local_machine',
  'e2b',
  'docker',
  'gcp_vm',
  'user_machine',
]);

/** Provider-normalized signal; unavailable data is represented explicitly, never as zero. */
export interface DeploymentObservation {
  metric: string;
  coverage: ObservationCoverage;
  value?: number | null;
  unit?: string | null;
  observed_at: string;
  window_start?: string | null;
  window_end?: string | null;
  source: string;
}

/** Provider-neutral placement coordinates. */
export interface DeploymentTarget {
  provider: string;
  scope: string;
  location?: string | null;
}

/**
 * Where this record's truth lives — the cloud resource being placed.
 *
 * Not a `CloudOrigin` (a record identity has a key from birth): a placement exists
 * before it is placed, so `external_id` stays empty until a node is allocated. Twin of
 * `PlacementOrigin` in `flow_sdk/builtin/deployment.py`. `external_id` is the ComputeNode
 * typeid for a node-backed placement, or the provider's own resource name for an
 * inventoried one.
 */
export interface PlacementOrigin {
  kind: string;
  provider: string;
  external_id: string;
  url?: string;
}

export interface DeploymentStatus {
  sync_state: DeploymentSyncState;
  provider_state?: string | null;
  observed_at?: string | null;
  message?: string | null;
}

/** One service a deployment declares it exposes. Twin of `EndpointDeclaration`. */
export interface EndpointDeclaration {
  name: string;
  subkind: EndpointSubkind;
  protocol: ServiceProtocol;
  backend?: ServiceBackend | null;
  check?: HealthCheck | null;
}

export interface IDeployment extends Omit<IEntity, 'status'> {
  name: string;
  /** Every service this deployment declares it exposes; its endpoint rows serve the declaration. */
  exposes?: EndpointDeclaration[];
  /** Who the box logs in as for this placement. What it places is its PARENT (`elementType`). */
  identity?: DeploymentIdentity;
  artifact_id?: string | null;
  artifact_link_source?: ArtifactLinkSource | null;
  target: DeploymentTarget;
  origin?: PlacementOrigin | null;
  status: DeploymentStatus;
  provider_labels: Record<string, string>;
  observations: Partial<Record<DeploymentObservationKind, DeploymentObservation>>;
  source_revision?: string | null;
  project_id?: string | null;
  /** Credential environment: `development` (this computer) or a named one (`production`, `staging`, …). */
  environment?: string;
  llm_endpoint_typeid?: string;
}

// `implements IDeployment` only checks the class; it contributes no members, so every
// field declared solely on IDeployment read as "does not exist". deepAssign populates
// them from the wire — this merge makes them part of the class type.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface Deployment extends EntityMerge<IDeployment> {}

/** One thing that happened on a deployment — mirror of `deployment.timeline_event`. */
export interface TimelineEvent {
  at: string;
  kind: 'message_in' | 'reply_sent' | 'turn_started' | 'turn_failed' | 'refused';
  who: string;
  text: string;
  channel: string;
  data_source_id: string;
  process_id: string;
  conversation_id: string;
  message_id: string;
}

/** A page of a deployment's timeline, newest first — mirror of `deployment.timeline`. */
export interface DeploymentTimeline {
  deployment_id: string;
  events: TimelineEvent[];
  before: string | null;
}

/**
 * One conversation a deployment holds — a chat, a mail thread, a whole phone call — mirror of
 * `deployment.thread`. `status`: `live` (a call is on the line), `working` (the agent is mid-turn),
 * `ended` (a call that is over), `idle`.
 */
export interface DeploymentThread {
  conversation_id: string;
  title: string;
  who: string;
  channel: string;
  data_source_id: string;
  process_id: string;
  status: 'live' | 'working' | 'ended' | 'idle';
  started_at: string | null;
  last_at: string | null;
  last_text: string;
  messages: number;
  turns: number;
}

/** A local deployment's process — mirror of `deployment.process`: the file it runs, its terminal. */
export interface DeploymentProcess {
  deployment_id: string;
  /** The Shell whose terminal runs it; empty before its first start. */
  shell_id: string;
  file: string;
  command: string;
  /** The loop's pid while it runs, else null. */
  pid: number | null;
  serving: boolean;
}

/** The text of the file a local deployment runs — mirror of `deployment.code`. */
export interface DeploymentCode {
  file: string;
  text: string;
}

/** The tag a deployment's process emits when its timeline moved (relayed to the app). */
export const DEPLOYMENT_TIMELINE_TAG = 'deployment.timeline';

/**
 * Deployment is THE placement record: this thing runs on that machine.
 *
 * Two axes, each declared once — `kind` says WHAT is placed (`runtime.agent`,
 * `runtime.web`, `compute.node`), `target.provider` says WHERE (`local`, `e2b`,
 * `gcp`). The row is parented to the deployed element and holds the same id on
 * the hub and here, so a cloud placement is adopted rather than re-minted.
 */

/** One value the hub holds for a cloud placement. Names only; `ts` is epoch seconds. */
export interface DeploymentSecretRow {
  name: string;
  written?: number;
  placed?: { ts: number; event: 'placed' | 'unplaced' | string; detail?: string };
}

/** A connection a cloud placement's machine may use (`Deployment.authorize`). */
export interface DeploymentAuthorizationRow {
  provider: string;
  permissions: string[];
  ts?: number;
}

export interface DeploymentSecretsInventory {
  secrets: DeploymentSecretRow[];
  authorizations: DeploymentAuthorizationRow[];
}

/** What pays for a deployment's turns and whether it can pay for one now (hub `deployment/<id>/funding`). */
export interface DeploymentFunding {
  /** `allocation` (its own sub-endpoint), `shared`, `default` (its owner's capped default) or `none`. */
  kind: 'allocation' | 'shared' | 'default' | 'none' | string;
  endpoint_typeid: string;
  name: string;
  /** The size the agent asks for (`sm`/`md`/`lg`), when it names one. */
  size?: string | null;
  /** The model that size — or the pinned model — resolves to. */
  model?: string | null;
  /** Why the next turn would be refused; empty = it can be paid for. */
  exhausted: string;
}

@registerEntity
export class Deployment extends APIEntity<Deployment> implements IDeployment {
  static type: string = 'deployment';

  /** What pays for THIS deployment's turns, asked without spending. */
  async funding(): Promise<DeploymentFunding> {
    return dataManager.callAction<undefined, DeploymentFunding>(
      new ActionInfo('funding', Deployment.type, this.id, 'GET'),
    );
  }

  /**
   * The cloud compute providers a deployment may be placed on — the hub's list (`deployment/providers`,
   * relayed by the desktop), in the hub's order. Throws when the hub cannot be asked (409: signed out).
   * Cached through `LazyAsset.DeployProviders`.
   */
  static async providers(): Promise<CloudDeployProvider[]> {
    const rows = await dataManager.callAction<undefined, string[]>(
      new ActionInfo('providers', Deployment.type, null, 'GET'),
    );
    return Array.isArray(rows) ? rows.filter(isNonEmptyString) : [];
  }

  name: string;
  identity: DeploymentIdentity;
  exposes: EndpointDeclaration[];
  artifact_id: string | null;
  artifact_link_source: ArtifactLinkSource | null;
  target: DeploymentTarget;
  origin: PlacementOrigin | null;
  status: DeploymentStatus;
  provider_labels: Record<string, string>;
  observations: Partial<Record<DeploymentObservationKind, DeploymentObservation>>;
  source_revision: string | null;
  project_id: string | null;
  environment: string;
  /** Its own token allocation on the hub (an `llm_endpoint` typeid); '' = its agent spends its owner's default. */
  llm_endpoint_typeid: string;

  constructor(entity: Partial<IDeployment> | IEntity = {}) {
    super(entity);
    const deployment = entity as Partial<IDeployment>;
    this.name = deployment.name ?? '';
    this.identity = deployment.identity ?? 'user';
    this.exposes = deployment.exposes ?? [];
    this.artifact_id = deployment.artifact_id ?? null;
    this.artifact_link_source = deployment.artifact_link_source ?? null;
    this.target = {
      provider: deployment.target?.provider ?? '',
      scope: deployment.target?.scope ?? '',
      location: deployment.target?.location ?? null,
    };
    this.origin = deployment.origin
      ? {
          kind: deployment.origin.kind ?? '',
          provider: deployment.origin.provider ?? '',
          external_id: deployment.origin.external_id ?? '',
          url: deployment.origin.url ?? '',
        }
      : null;
    this.status = {
      sync_state: deployment.status?.sync_state ?? 'current',
      provider_state: deployment.status?.provider_state ?? null,
      observed_at: deployment.status?.observed_at ?? null,
      message: deployment.status?.message ?? null,
    };
    this.provider_labels = normalizeProviderLabels(deployment.provider_labels);
    this.observations = normalizeObservations(deployment.observations);
    this.source_revision = deployment.source_revision ?? null;
    this.project_id = deployment.project_id ?? null;
    this.environment = deployment.environment || DEFAULT_CREDENTIAL_ENVIRONMENT;
    this.llm_endpoint_typeid = deployment.llm_endpoint_typeid ?? '';
    this.validateStructure();
  }

  override get dockPointer(): DockPointerData {
    return Deployment.dockPointerFor(this.id);
  }

  /** This deployment's own page — nested in its agent's editor, where its funding, threads and health live —
   *  or `null` when it does not place an agent. */
  get agentPagePointer(): DockPointerData | null {
    const parent = this.parent_type_id;
    const editor = editorForType('agent');
    if (!editor || !parent || !isTypeId(parent) || new TypeId(parent).type !== 'agent') return null;
    // The nested-child options the agent editor reads (`DockPointer.withChild`, ui).
    return new DockPointerData(ViewType.ASSETS, `editor/${editor}/typeid/${parent}`, {
      child: `${Deployment.type}-${this.id}`,
      child_section: 'deployment',
    });
  }

  /** Where deployment *id* opens, without loading it — an issue's "Open the deployment" names only the id. */
  static dockPointerFor(id: string): DockPointerData {
    return new DockPointerData(ViewType.WORLDVIEW, WorldViewProjection.DEPLOYMENT, {
      focus: `${Deployment.type}-${id}`,
      selected: `${Deployment.type}-${id}`,
    });
  }

  /** What this places — its parent's type (`agent`, `project`, `compute_node`, …), or null. */
  get elementType(): string | null {
    const parent = this.parent_type_id;
    if (!parent || !isTypeId(parent)) return null;
    return new TypeId(parent).type;
  }

  /** The Agent this places, or null when the deployed element is something else. */
  get agentTypeId(): TypeId | null {
    const parent = this.parent_type_id;
    if (!parent || !isTypeId(parent)) return null;
    const typeId = new TypeId(parent);
    return typeId.type === 'agent' ? typeId : null;
  }

  /** The id of the machine this runs on, or null when there is none or the stored id is not a typeid. */
  get computeNodeId(): string | null {
    const typeId = this.computeNodeTypeId;
    if (!typeId || !isTypeId(typeId)) return null;
    const parsed = new TypeId(typeId);
    return parsed.type === 'compute_node' ? parsed.id : null;
  }

  /** The machine this runs on, or null when the placement is not node-backed. */
  get computeNodeTypeId(): string | null {
    if (!NODE_PROVIDERS.has(this.target.provider)) return null;
    return this.origin?.external_id || null;
  }

  /**
   * Stop the machine, keep the row.
   *
   * Terminate is a pause: this row carries the placement's cost and activity
   * observations, and deleting it throws away the only record of what the box
   * cost. Deleting a Deployment destroys a real cloud resource, which is why
   * that path warns and this one does not.
   */
  async pause(): Promise<Deployment> {
    return new Deployment((await this.post('pause')) as IDeployment);
  }

  /** Start a paused machine again. */
  async resume(): Promise<Deployment> {
    return new Deployment((await this.post('resume')) as IDeployment);
  }

  /** Bring a cloud machine to the published definition (the hub re-clones and re-indexes it). */
  async update(): Promise<Record<string, unknown>> {
    return ((await this.post('update')) ?? {}) as Record<string, unknown>;
  }

  /** What the hub holds for this cloud placement — each value's name, last write and last placement
   *  on its machine, and the connections its machine may use. Names only. */
  async secretsInventory(): Promise<DeploymentSecretsInventory> {
    const data = await this.get<Partial<DeploymentSecretsInventory> | null>('secrets');
    return { secrets: data?.secrets ?? [], authorizations: data?.authorizations ?? [] };
  }

  /** Let this placement's machine use your `provider` connection: it asks the hub for a fresh token
   *  when it needs one; your refresh token never leaves the hub. */
  async authorize(provider: string, permissions: string[] = []): Promise<void> {
    await this.post('authorize', { provider, permissions });
  }

  /** Take `provider` back: the machine's next token ask is refused. */
  async revoke(provider: string): Promise<void> {
    await this.post('authorize', { provider, revoke: true });
  }

  /** As healthy as its least healthy service, by each endpoint's last check. Derived, never stored. */
  async health(): Promise<HealthState> {
    return worstHealth((await this.endpoints()).map((e) => e.health?.state ?? 'unknown'));
  }

  /** What this placement serves — for a cloud placement, as the hub has it now (held here at the hub's ids). */
  async endpoints(): Promise<ServiceEndpoint[]> {
    const data = await this.get<{ endpoints?: IServiceEndpoint[] } | null>('endpoints');
    return (data?.endpoints ?? []).map((row) => new ServiceEndpoint(row));
  }

  /**
   * What reached this deployment, what it ran and what it answered — newest first
   * (`GET deployment/<id>/timeline`); `conversation` narrows it to one thread, `before` pages back.
   */
  async timeline(
    opts: { limit?: number; before?: string | null; conversation?: string | null } = {},
  ): Promise<DeploymentTimeline> {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.before) params.set('before', opts.before);
    if (opts.conversation) params.set('conversation', opts.conversation);
    const qs = params.toString();
    const data = await this.get<DeploymentTimeline | null>(`timeline${qs ? `?${qs}` : ''}`);
    return data ?? { deployment_id: this.id, events: [], before: null };
  }

  /** A local deployment's process: its file, its terminal, its pid (`GET deployment/<id>/process`). */
  async process(): Promise<DeploymentProcess | null> {
    return this.get<DeploymentProcess | null>('process');
  }

  /** The Python file a local deployment runs (`GET deployment/<id>/code`). */
  async code(): Promise<DeploymentCode | null> {
    return this.get<DeploymentCode | null>('code');
  }

  /** Write the file; it runs from the next (re)start (`POST deployment/<id>/save_code`). */
  async saveCode(text: string): Promise<DeploymentCode> {
    return (await this.post('save_code', { text })) as DeploymentCode;
  }

  /** Stop the loop; the app runs the file again at once, in the same terminal (`POST deployment/<id>/restart`). */
  async restart(): Promise<DeploymentProcess> {
    return (await this.post('restart')) as DeploymentProcess;
  }

  /** The conversations it holds, the active ones first (`GET deployment/<id>/threads`). */
  async threads(): Promise<DeploymentThread[]> {
    const data = await this.get<{ threads?: DeploymentThread[] } | null>('threads');
    return data?.threads ?? [];
  }

  /** The latest runs on a cloud machine, read through the hub. This computer's runs are the local run list. */
  async runs<T = Record<string, unknown>>(limit: number): Promise<T[]> {
    const data = await this.get<{ runs?: T[] } | null>(`runs?limit=${encodeURIComponent(String(limit))}`);
    return data?.runs ?? [];
  }

  private validateStructure(): void {
    const errors: string[] = [];
    if (!isNonEmptyString(this.name)) errors.push('name is required');
    if (!isNonEmptyString(this.target.provider)) errors.push('target.provider is required');
    if (!isNonEmptyString(this.target.scope)) errors.push('target.scope is required');
    if (!['current', 'stale', 'partial', 'error'].includes(this.status.sync_state)) {
      errors.push(`invalid status.sync_state: ${this.status.sync_state}`);
    }
    if (this.artifact_link_source && !['manual', 'gcp_label'].includes(this.artifact_link_source)) {
      errors.push(`invalid artifact_link_source: ${this.artifact_link_source}`);
    }
    if (errors.length > 0) {
      throw new Error(`Invalid Deployment structure: ${errors.join(', ')}`);
    }
  }
}

function normalizeObservations(
  observations: Partial<Record<DeploymentObservationKind, DeploymentObservation>> | undefined,
): Partial<Record<DeploymentObservationKind, DeploymentObservation>> {
  if (observations === undefined) return {};
  if (!observations || Array.isArray(observations) || typeof observations !== 'object') {
    throw new Error('Invalid Deployment structure: observations must be an object');
  }
  const normalized: Partial<Record<DeploymentObservationKind, DeploymentObservation>> = {};
  const supportedKinds = new Set<DeploymentObservationKind>(['cost', 'size', 'activity']);
  for (const key of Object.keys(observations)) {
    if (!supportedKinds.has(key as DeploymentObservationKind)) {
      throw new Error(`Invalid Deployment structure: invalid observation kind: ${key}`);
    }
  }
  for (const kind of ['cost', 'size', 'activity'] as const) {
    const observation = observations[kind];
    if (!observation || typeof observation !== 'object') continue;
    const coverage = observation.coverage ?? 'available';
    const value = observation.value ?? null;
    const hasValue = typeof value === 'number' && Number.isFinite(value);
    if (!['available', 'unavailable', 'unattributed', 'stale'].includes(coverage)) {
      throw new Error(`Invalid Deployment structure: invalid observations.${kind}.coverage: ${coverage}`);
    }
    if ((coverage === 'available' || coverage === 'stale') && (!hasValue || !isNonEmptyString(observation.unit))) {
      throw new Error(`Invalid Deployment structure: observations.${kind} requires a finite value and unit`);
    }
    if ((coverage === 'unavailable' || coverage === 'unattributed') && observation.value != null) {
      throw new Error(`Invalid Deployment structure: observations.${kind} must not carry a value`);
    }
    if (!isNonEmptyString(observation.observed_at) || !isNonEmptyString(observation.source)) {
      throw new Error(`Invalid Deployment structure: observations.${kind} requires observed_at and source`);
    }
    if (!isNonEmptyString(observation.metric)) {
      throw new Error(`Invalid Deployment structure: observations.${kind} requires metric`);
    }
    const observedAt = normalizeTimestamp(observation.observed_at, `observations.${kind}.observed_at`);
    const source = observation.source.trim();
    const unit = normalizeOptionalText(observation.unit, `observations.${kind}.unit`);
    const windowStart = normalizeOptionalTimestamp(observation.window_start, `observations.${kind}.window_start`);
    const windowEnd = normalizeOptionalTimestamp(observation.window_end, `observations.${kind}.window_end`);
    if ((windowStart === null) !== (windowEnd === null)) {
      throw new Error(`Invalid Deployment structure: observations.${kind} window requires both endpoints`);
    }
    if (windowStart && windowEnd && Date.parse(windowStart) >= Date.parse(windowEnd)) {
      throw new Error(`Invalid Deployment structure: observations.${kind} window_start must be before window_end`);
    }
    if ((kind === 'cost' || kind === 'activity') && (!windowStart || !windowEnd)) {
      throw new Error(`Invalid Deployment structure: ${kind} observation requires a declared window`);
    }
    normalized[kind] = {
      metric: normalizeKind(observation.metric),
      coverage,
      value,
      unit,
      observed_at: observedAt,
      window_start: windowStart,
      window_end: windowEnd,
      source,
    };
  }
  return normalized;
}

const RFC3339_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-](\d{2}):(\d{2}))$/;

function normalizeTimestamp(value: unknown, field: string): string {
  if (typeof value !== 'string') throw new Error(`Invalid Deployment structure: ${field} must be a string`);
  const timestamp = value.trim();
  const match = RFC3339_PATTERN.exec(timestamp);
  if (!match || !isValidRfc3339Parts(match) || Number.isNaN(Date.parse(timestamp))) {
    throw new Error(`Invalid Deployment structure: ${field} must be RFC3339 with a timezone`);
  }
  return timestamp;
}

function isValidRfc3339Parts(match: RegExpExecArray): boolean {
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === undefined ? 0 : Number(match[8]);
  const offsetMinute = match[9] === undefined ? 0 : Number(match[9]);
  if (year < 1 || month < 1 || month > 12 || hour > 23 || minute > 59 || second > 59) return false;
  if (offsetHour > 23 || offsetMinute > 59) return false;
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day >= 1 && day <= days[month - 1];
}

function normalizeOptionalTimestamp(value: unknown, field: string): string | null {
  if (value === undefined || value === null) return null;
  return normalizeTimestamp(value, field);
}

function normalizeOptionalText(value: unknown, field: string): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'string') throw new Error(`Invalid Deployment structure: ${field} must be a string`);
  return value.trim() || null;
}

function normalizeProviderLabels(labels: Record<string, string> | undefined): Record<string, string> {
  if (!labels || Array.isArray(labels) || typeof labels !== 'object') return {};
  return Object.fromEntries(
    Object.entries(labels).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
  );
}
